<template>
  <AuthCompleteLayout
    variant="auth"
    class="auth-layout"
  >
    <template #left>
      <div class="auth-layout__glass">
        <!-- Login: Gleap news loading -->
        <div
          v-if="showLoginMode && newsLoading && !news.length"
          class="auth-layout__news-loading"
        >
          <i class="pi pi-spin pi-spinner" />
        </div>

        <!-- Login: Gleap news carousel -->
        <Carousel
          v-else-if="showLoginMode && news.length"
          :value="news"
          :num-visible="1"
          :num-scroll="1"
          :show-navigators="false"
          :show-indicators="true"
          circular
          :autoplay-interval="6000"
        >
          <template #item="slotProps">
            <a
              class="slide slide--feature slide--news"
              :href="buildNewsArticleUrl(slotProps.data.id)"
              target="_blank"
              rel="noopener noreferrer"
            >
              <h2 class="slide__title">
                {{ slotProps.data.subject }}
              </h2>
              <div
                v-if="slotProps.data.coverImageUrl"
                class="slide__img-frame"
              >
                <div
                  class="slide__img"
                  :style="{ backgroundImage: 'url(' + slotProps.data.coverImageUrl + ')' }"
                />
              </div>
              <p
                v-if="slotProps.data.description"
                class="slide__desc"
              >
                {{ slotProps.data.description }}
              </p>
            </a>
          </template>
        </Carousel>

        <!-- Registration OR login fallback: testimonials -->
        <Carousel
          v-else
          :value="testimonials"
          :num-visible="1"
          :num-scroll="1"
          :show-navigators="false"
          :show-indicators="true"
          circular
          :autoplay-interval="5000"
        >
          <template #item="slotProps">
            <div class="slide slide--testimonial">
              <div class="slide__quote-mark">
                &ldquo;
              </div>
              <blockquote class="slide__quote">
                {{ slotProps.data.title }}
              </blockquote>
              <div class="slide__sep" />
              <div
                class="slide__brand"
                :style="{ backgroundImage: 'url(' + slotProps.data.image + ')' }"
              />
              <p class="slide__name">
                {{ slotProps.data.author }}
              </p>
              <p class="slide__role">
                {{ slotProps.data.position }}
              </p>
              <div class="slide__stars">
                <i
                  v-for="n in 5"
                  :key="n"
                  class="fa-solid fa-star"
                />
              </div>
            </div>
          </template>
        </Carousel>
      </div>
    </template>

    <router-view />
  </AuthCompleteLayout>
</template>

<script lang="ts">
import { computed, defineComponent, watch } from 'vue';
import Carousel from 'primevue/carousel';
import { useRouter } from 'vue-router';
import AuthCompleteLayout from '@/views/layout/AuthCompleteLayout.vue';
import { buildNewsArticleUrl, useGleapNews } from '@/composables/useGleapNews';

export default defineComponent({
  name: 'LayoutAuthentication',

  components: {
    Carousel,
    AuthCompleteLayout,
  },

  setup() {
    const router = useRouter();
    const { news, loading: newsLoading, load: loadNews } = useGleapNews();

    const testimonials = [
      {
        title: 'Nous avons été séduits par sa facilité d\'utilisation et surtout par l\'accompagnement des équipes ShopiMind',
        image: '/images/logos-testimonials/archiduchesse.png',
        author: 'Pauline Calvo',
        position: 'Cheffe de projet E-commerce',
      },
      {
        title: 'Après un benchmark Shopimind semblait être la solution qui permettait de répondre à une grande partie de nos besoins',
        image: '/images/logos-testimonials/speedway.svg',
        author: 'Rodolphe Ricard',
        position: 'Responsable Acquisition',
      },
      {
        title: 'Une solution performante que nous utilisons depuis plusieurs années. L\'équipe est réactive et très sympathique (ce qui est très appréciable)',
        image: '/images/logos-testimonials/generationsouvenir.png',
        author: 'Sébastien Surget',
        position: 'Directeur',
      },
      {
        title: 'Très bon rapport qualité prix pour ce logiciel d\'abandon de panier omnicanal. Je recommande',
        image: '/images/logos-testimonials/beelong.svg',
        author: 'Benoit Delporte',
        position: 'Co-Fondateur',
      },
    ];

    const showLoginMode = computed(
      () => router.currentRoute.value.name !== 'user.registration-speed',
    );

    watch(() => router.currentRoute.value.name, (route) => {
      if (route !== 'user.registration-speed') {
        loadNews(9);
      }
    }, { immediate: true });

    return {
      testimonials,
      news,
      newsLoading,
      showLoginMode,
      buildNewsArticleUrl,
    };
  },
});
</script>

<style scoped lang="scss">
.auth-layout {
  &__glass {
    width: 100%;
    max-width: 440px;
    margin: 0 auto;
    background: rgba(255, 255, 255, 0.04);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 20px;
    padding: 2.25rem 2rem;
  }

  &__news-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 220px;
    i {
      font-size: 1.5rem;
      color: #94c840;
      opacity: 0.8;
    }
  }
}

.slide--news {
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: transform 0.25s ease;

  &:hover {
    transform: translateY(-2px);

    .slide__title { color: #b8e05f; }
  }
}

/* Testimonial slides */
.slide--testimonial {
  text-align: center;
  padding: 0 0.5rem;
}

.slide__quote-mark {
  font-size: 3rem;
  line-height: 1;
  color: #94c840;
  font-family: Georgia, serif;
  opacity: 0.65;
  margin-bottom: -0.25rem;
}

.slide__quote {
  font-size: 1.0625rem;
  font-weight: 300;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.88);
  font-style: italic;
  margin: 0 0 1.5rem;
  min-height: 4.5rem;
  quotes: none;
}

.slide__sep {
  width: 36px;
  height: 2px;
  background: #94c840;
  margin: 0 auto 1.25rem;
  border-radius: 2px;
}

.slide__brand {
  width: 120px;
  height: 40px;
  margin: 0 auto 1rem;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  filter: brightness(0) invert(1);
  opacity: 0.45;
}

.slide__name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.125rem;
}

.slide__role {
  font-size: 0.75rem;
  color: #94c840;
  margin-bottom: 0.625rem;
}

.slide__stars {
  display: flex;
  justify-content: center;
  gap: 0.2rem;
  i { font-size: 0.6875rem; color: #fbbf24; }
}

/* Feature slides */
.slide--feature {
  text-align: center;
  padding: 0 0.25rem;
}

.slide__title {
  font-size: 1.1875rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.45;
  margin-bottom: 1.5rem;
}

.slide__img-frame {
  margin: 0 auto 1.5rem;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(148, 200, 64, 0.1);
}

.slide__img {
  width: 100%;
  padding-bottom: 58%;
  background-size: cover;
  background-position: center;
}

.slide__desc {
  font-size: 0.8125rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.45);
  max-width: 360px;
  margin: 0 auto;
}

/* Carousel indicators */
.auth-layout {
  :deep(.p-carousel) {
    .p-carousel-content { overflow: hidden; }

    .p-carousel-indicators {
      padding: 1.25rem 0 0;

      .p-carousel-indicator {
        margin: 0 3px;

        button {
          width: 24px;
          height: 3px;
          border-radius: 3px;
          background-color: rgba(255, 255, 255, 0.12);
          border: none;
          padding: 0;
          transition: all 0.35s ease;
        }

        &.p-highlight button {
          background-color: #94c840;
          width: 32px;
        }
      }
    }
  }
}
</style>
